"use client";

import { forwardRef, useRef, useState, useEffect, useContext, Children } from "react";
import styled, { css } from "styled-components";
import { accentColorPrimary, backgroundColorAlt, spacing4, spacing8, spacing16, spacing32, boxShadowRaised, lineHeight } from "@churchofjesuschrist/eden-style-constants";
import { dir } from "@churchofjesuschrist/eden-styled-helpers";
import { RangeInputContext, IdContext, DescribedByContext } from "../utils/context";
import { A11y } from "@churchofjesuschrist/eden-buttons";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const handleCss = css(["appearance:none;border:none;background:", ";width:", ";height:", ";border-radius:50%;box-shadow:", ";z-index:3;"], accentColorPrimary, spacing16, spacing16, boxShadowRaised);
export const InternalUseOnlyRangeInput = styled.input.withConfig({
  componentId: "sc-9nm6bc-0"
})(["isolation:isolate;line-height:", ";appearance:none;width:100%;height:", ";margin:0.375rem 0;cursor:pointer;background:linear-gradient( to right,", " 0%,", " var(--lower-range-percentage),", " var(--lower-range-percentage),", " 100% );&::-moz-focus-outer{border:0;}", " &::-webkit-slider-thumb{", ";}&::-moz-range-thumb{", ";}"], lineHeight, spacing4, accentColorPrimary, accentColorPrimary, backgroundColorAlt, backgroundColorAlt, dir("rtl")`
    background: linear-gradient(
      to left,
      ${accentColorPrimary} 0%,
      ${accentColorPrimary}
        var(--lower-range-percentage),
      ${backgroundColorAlt} var(--lower-range-percentage),
      ${backgroundColorAlt} 100%
    );
  `, handleCss, handleCss);
const Ticks = styled.div.withConfig({
  componentId: "sc-9nm6bc-1"
})(["position:relative;display:flex;align-items:start;justify-content:start;width:calc(100% - ", ");margin-block-start:-0.375rem;margin-inline-start:", ";", ";"], spacing16, spacing8, ({
  $writingMode
}) => $writingMode !== "horizontal-tb" ? css(["align-items:start;"]) : "");
const Tick = styled(A11y).withConfig({
  componentId: "sc-9nm6bc-2"
})(["position:relative;width:0;display:flex;justify-content:center;align-items:center;flex-wrap:wrap;"]);
const TickMark = styled.span.withConfig({
  componentId: "sc-9nm6bc-3"
})(["min-width:", ";padding-block-start:0.375rem;text-align:center;font-size:6px;"], spacing32);
const TickContent = styled.span.withConfig({
  componentId: "sc-9nm6bc-4"
})(["padding-top:", ";display:flex;align-items:center;justify-content:center;min-width:", ";", ";"], spacing4, spacing32, ({
  $writingMode
}) => $writingMode !== "horizontal-tb" ? `writing-mode: ${$writingMode};` : "");

/**
 * The `RangeInput` allows a user to specify a numeric value which must be no less than a given value, and no more than another given value.
 * @param {object} props - The component props
 * @param {string} [props.aria-describedby] - Identifies the element (or elements) that describes the `RangeInput`
 * @param {string} [props.defaultValue] - Default value for uncontrolled use
 * @param {boolean} [props.disabled] - Makes the input not mutable, focusable, or even submitted with the form
 * @param {string} [props.id] - An identifier which must be unique in the whole document
 * @param {string} [props.max] - The `RangeInput` value must be less than or equal to the max value
 * @param {string} [props.min] - The `RangeInput` value must be greater than or equal to the min value
 * @param {string} [props.name] - Used to reference the `RangeInput` in JavaScript, or to reference form data after a form is submitted
 * @param {Function} [props.onChange] - Callback to execute when the value changes
 * @param {string} [props.step] - Unless the step is set to the any literal, the input value must be min + an integral multiple of the step
 * @param {string} [props.value] - The value of the `RangeInput` for controlled use
 * @param {object} [props.style] - Additional styles to be applied to the RangeInput
 * @param {React.ReactNode} [props.children] - Defines a series of [tick marks](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/range#adding_tick_marks) on the `RangeInput`. Should be `<option>` elements.
 * @param {("horizontal-tb"|"vertical-lr"|"vertical-rl")} [props.writingMode] - Sets [writingMode](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode) of the tick labels
 * @param {React.Ref} [forwardedRef] - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The RangeInput component
 */
const RangeInputComponent = ({
  "aria-describedby": ariaDescribedBy,
  defaultValue,
  id,
  max = "100",
  min = "0",
  onChange,
  step = "1",
  value,
  style,
  children,
  writingMode = "horizontal-tb",
  ...props
}, forwardedRef) => {
  const rangeInputContext = useContext(RangeInputContext);
  const idContext = useContext(IdContext);
  const describedByContext = useContext(DescribedByContext);
  const [stateValue, setStateValue] = useState(value ?? defaultValue ?? "0");
  const internalRef = useRef();
  const ref = forwardedRef ?? internalRef;
  useEffect(() => {
    const closestForm = ref.current.form;
    if (!closestForm) {
      return;
    }
    const resetHandler = () => {
      setStateValue(value ?? defaultValue ?? "0");
    };
    closestForm.addEventListener("reset", resetHandler);
    return () => closestForm.removeEventListener("reset", resetHandler);
  });
  useEffect(() => {
    setStateValue(value ?? defaultValue ?? "0");
  }, [value, defaultValue]);
  const handleChange = event => {
    //If a child without value is the target use the currentTarget instead
    event.target = event.target.value ? event.target : event.currentTarget;
    event.currentTarget = ref.current;
    setStateValue(event.target.value);
    onChange?.(event);
  };
  const calculatePercentageFromStart = to => {
    return (parseFloat(to) - parseFloat(rangeInputContext?.min || min)) / (parseFloat(rangeInputContext?.max || max) - parseFloat(rangeInputContext?.min || min)) * 100;
  };
  const calculateBackgroundVars = () => {
    const lowerRangePercentage = calculatePercentageFromStart(stateValue);
    return {
      "--lower-range-percentage": `${lowerRangePercentage}%`
    };
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(InternalUseOnlyRangeInput, {
      style: {
        ...calculateBackgroundVars(),
        ...style
      },
      ref: ref,
      "aria-describedby": ariaDescribedBy ? ariaDescribedBy : describedByContext,
      id: id ? id : idContext,
      max: rangeInputContext?.max || max,
      min: rangeInputContext?.min || min,
      onChange: handleChange,
      step: rangeInputContext?.step || step,
      type: "range",
      value: stateValue,
      ...props
    }), children ? /*#__PURE__*/_jsx(Ticks, {
      "aria-hidden": "true",
      $writingMode: writingMode,
      children: Children.toArray(children).map(({
        props
      }, i) => {
        const valueFromProps = props?.value ?? props?.children;
        const labelFromProps = props?.children ?? props?.label;
        return /*#__PURE__*/_jsxs(Tick, {
          tabIndex: "-1",
          style: {
            insetInlineStart: `${calculatePercentageFromStart(valueFromProps)}%`
          },
          value: valueFromProps,
          onClick: handleChange,
          children: [/*#__PURE__*/_jsx(TickMark, {
            children: "|"
          }), /*#__PURE__*/_jsx(TickContent, {
            $writingMode: writingMode,
            children: labelFromProps
          })]
        }, i);
      })
    }) : null]
  });
};
const RangeInput = /*#__PURE__*/forwardRef(RangeInputComponent);
RangeInput.displayName = "RangeInput";

// Custom property for the FormField to consume
RangeInput.isInput = true;
export default RangeInput;